import {
  removeProject,
  getAllProjects,
  shareProjectInMinervaNet,
  unshareProjectInMinervaNet,
  archiveProject,
  reviveProject,
  refreshProjectOnList,
} from '@/redux/projects/projects.thunks';
import { ProjectsState } from '@/redux/projects/projects.types';
import { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { getProjectById } from '@/redux/project/project.thunks';
import { ONE, ZERO } from '@/constants/common';
import { PROJECT_STATUSES } from '@/redux/projects/projects.constants';

export const getProjectsReducer = (builder: ActionReducerMapBuilder<ProjectsState>): void => {
  builder.addCase(getAllProjects.pending, state => {
    state.loading = 'pending';
  });
  builder.addCase(getAllProjects.fulfilled, (state, action) => {
    state.data = action.payload || undefined;
    state.loading = 'succeeded';
  });
  builder.addCase(getAllProjects.rejected, state => {
    state.loading = 'failed';
  });
};

export const getProjectByIdReducer = (builder: ActionReducerMapBuilder<ProjectsState>): void => {
  builder.addCase(getProjectById.fulfilled, (state, action) => {
    let exist = false;
    for (let i = 0; i < state.data.length; i += ONE) {
      const project = state.data[i];
      if (project.projectId === action.payload?.projectId) {
        exist = true;
        state.data[i] = action.payload;
      }
    }
    if (!exist && action.payload) {
      state.data.push(action.payload);
    }
  });
};

export const refreshProjectOnListReducer = (
  builder: ActionReducerMapBuilder<ProjectsState>,
): void => {
  builder.addCase(refreshProjectOnList.fulfilled, (state, action) => {
    if (!action.payload) {
      return;
    }
    // renaming changes projectId (the message already carries the new one), the numeric id stays
    const index = state.data.findIndex(project => project.id === action.payload?.id);
    if (index >= ZERO) {
      state.data[index] = action.payload;
    }
  });
};

export const removeProjectReducer = (builder: ActionReducerMapBuilder<ProjectsState>): void => {
  builder
    .addCase(removeProject.pending, state => {
      state.loading = 'pending';
      state.error = { name: '', message: '' };
    })
    .addCase(removeProject.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = 'succeeded';
      const project = state.data.find(p => p.projectId === action.payload);
      if (project) {
        project.status = PROJECT_STATUSES.REMOVING;
      }
    })
    .addCase(removeProject.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = { name: 'Remove Error', message: action.payload || 'Unknown error occurred' };
    });
};

export const shareProjectReducer = (builder: ActionReducerMapBuilder<ProjectsState>): void => {
  builder
    .addCase(shareProjectInMinervaNet.fulfilled, (state, action: PayloadAction<string>) => {
      const project = state.data.find(p => p.projectId === action.payload);
      if (project) {
        project.sharedInMinervaNet = true;
      }
    })
    .addCase(unshareProjectInMinervaNet.fulfilled, (state, action: PayloadAction<string>) => {
      const project = state.data.find(p => p.projectId === action.payload);
      if (project) {
        project.sharedInMinervaNet = false;
      }
    });
};

export const archiveProjectReducer = (builder: ActionReducerMapBuilder<ProjectsState>): void => {
  builder
    .addCase(archiveProject.pending, state => {
      state.loading = 'pending';
      state.error = { name: '', message: '' };
    })
    .addCase(archiveProject.fulfilled, (state, action) => {
      state.loading = 'succeeded';
      const project = state.data.find(p => p.projectId === action.payload);
      if (project) {
        project.status = PROJECT_STATUSES.ARCHIVED;
      }
    })
    .addCase(archiveProject.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = {
        name: 'Archive Error',
        message: action.payload || 'Failed to archive project',
      };
    });
};

export const reviveProjectReducer = (builder: ActionReducerMapBuilder<ProjectsState>): void => {
  builder
    .addCase(reviveProject.pending, state => {
      state.loading = 'pending';
      state.error = { name: '', message: '' };
    })
    .addCase(reviveProject.fulfilled, (state, action) => {
      state.loading = 'succeeded';
      const project = state.data.find(p => p.projectId === action.payload);
      if (project) {
        project.status = PROJECT_STATUSES.DONE;
      }
    })
    .addCase(reviveProject.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = {
        name: 'Unarchive Error',
        message: action.payload || 'Failed to unarchive project',
      };
    });
};
